<template>
    <section id="registered_user">
        <div class="page-breadcrumbs">
            <page-breadcrumbs :list="breadcrumbs"/>
        </div>
        <h2 class="page-title">
            Registered Users
        </h2>
        <div class="page-section page-section--bordered-form">
            <ss-user-registered-filter
                v-model="filters"
            />
        </div>
        <div
            v-if="$auth.check('registered_ss_users.delete') && userSelected.length > 0"
            class="page-section"
        >
            <el-button
                :loading="deleting"
                type="danger"
                plain
                icon="delete"
                @click="handleDelete"
            >
                Delete selected users
            </el-button>
        </div>

        <!-- table -->
        <el-table
            ref="usersTableRef"
            v-loading="usersLoading"
            :data="users"
            highlight-current-row
            class="w-100"
            @selection-change="handleSelectionChange"
        >
            <el-table-column
                type="selection"
                width="55"
            />
            <el-table-column
                prop="name"
                label="Name"
                min-width="180"
            />
            <el-table-column
                prop="email"
                label="Email"
                min-width="200"
            />
            <el-table-column
                prop="client_level"
                label="Client Level"
                min-width="200"
            >
                <template #default="scope">
                    {{ scope.row.client_level }}
                </template>
            </el-table-column>
            <el-table-column
                label="Coaching"
                prod="coaching"
                min-width="100"
            >
                <template #default="scope">
                    <el-switch
                        v-if="$auth.check('registered_ss_users.coaching')"
                        v-model="scope.row.coaching"
                        :loading="enablingCoaching[scope.row._id]"
                        style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
                        @change="toggleCoaching(scope.row)"
                    />
                </template>
            </el-table-column>
            <el-table-column
                v-if="$auth.check('registered_ss_users.directed_spend')"
                label="Directed Spend"
                prod="directed_spend"
                min-width="100"
            >
                <template #default="scope">
                    <el-switch
                        v-model="scope.row.directed_spend"
                        :loading="enablingDirectedSpend[scope.row._id]"
                        style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
                        @click="toggleDirectedSpend(scope.row)"
                        :disabled="!(scope.row.type !== 'child' || scope.row.is_fdm == 'true')"
                    />
                </template>
            </el-table-column>
            <el-table-column
                prop="aspiration"
                label="Aspiration"
                min-width="120"
            />
            <el-table-column
                prop="personal_type"
                label="Personality"
                min-width="110"
            />
            <el-table-column
                prop="role"
                label="Role"
                min-width="120"
            >
                <template #default="scope">
                    <div v-html="getUserRole(scope.row)"/>
                </template>
            </el-table-column>
            <el-table-column
                label="Step"
                min-width="150"
                show-overflow-tooltip
            >
                <template #default="scope">
                    <template v-if="scope.row.last_step.path != null">
                        <div
                            class="el-link--wrap"
                            @click="handleShowSteps(scope.row._id)"
                            v-html="modifyString(scope.row.last_step.path, '/')"
                        />
                    </template>
                    <template v-else>
                        {{ scope.row.last_step.path }}
                    </template>
                </template>
            </el-table-column>
            <el-table-column
                prop="points"
                label="Points"
                min-width="120"
            />
            <el-table-column
                prop="log"
                label="Log"
                min-width="100"
            >
                <template #default="scope">
                    <el-button
                        type="primary"
                        size="small"
                        icon="tickets"
                        @click="handleShowLog(scope.row._id)"
                    >
                        Show
                    </el-button>
                </template>
            </el-table-column>
            <el-table-column
                prop="status"
                label="Enabled"
                min-width="100"
            >
                <template #default="scope">
                    <el-switch
                        v-if="$auth.check('registered_ss_users.disable')"
                        v-model="scope.row.status"
                        :loading="enabling[scope.row._id]"
                        style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
                        @change="toggleEnable(scope.row)"
                    />
                </template>
            </el-table-column>
            <el-table-column
                label="Actions"
                min-width="90"
                align="right"
            >
                <template #default="scope">
                    <el-button
                        v-if="$auth.check('registered_ss_users.edit')"
                        size="small"
                        icon="edit"
                        @click="$router.push({name: 'ss-users.edit', params: {id: scope.row._id}})"
                    >
                        Edit
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- pagination -->
        <el-pagination
            v-model:current-page="page"
            :page-size="perPage"
            :total="total"
            hide-on-single-page
            layout="prev, pager, next, ->, total"
            class="el-pagination--align-right"
            @current-change="fetchData"
            @size-change="fetchData"
        />

        <el-dialog
            v-model="showStepsModal"
            destroy-on-close
            title="Visited pages"
        >
            <UserSteps :user-id="userId"/>
        </el-dialog>

        <el-dialog
            v-model="showLogModal"
            destroy-on-close
            title="Logs"
        >
            <UserLogs :user-id="userId"/>
        </el-dialog>
    </section>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue'
import {ElMessageBox} from 'element-plus'
import ssUserApi from '../ssUserApi'
import SsUserRegisteredFilter from './SsUserRegisteredFilter'
import UserLogs from './UserLogs'
import UserSteps from './UserSteps'
import PageBreadcrumbs from '~/base/components/page-breadcrumbs/PageBreadcrumbs'
import ClientLevelsApi from "~/modules/client_levels/clientLevelsApi";

const usersLoading = ref(false)
const users = ref(null)
const total = ref(0)
const filters = ref({currently_on: true})
const page = ref(1)
const perPage = ref(10)
const usersTableRef = ref()
const userSelected = ref([])
const clientLevels = ref([])
const isClientLevelLoaded = ref(false)
const isUsersLoaded = ref(false)
const deleting = ref(false)
const breadcrumbs = ref([
    {
        title: '..',
        path: '/'
    },
    {
        title: 'Registered Users',
    }
])

onMounted(() => {
    fetchClientLevels()
    fetchData()
})

watch(
    () => filters.value,
    val => applySearch(),
    {deep: true}
)

function fetchClientLevels() {
    ClientLevelsApi.all().then((response) => {
        clientLevels.value = response.data
        isClientLevelLoaded.value = true
        getClientLevelPath()
    })
}

async function fetchData() {
    usersLoading.value = true
    isUsersLoaded.value = false
    let params = {
        page: page.value,
        ...filters.value
    }
    const {data} = await ssUserApi.registeredUsers(params)
    users.value = data.data
    total.value = data.total
    perPage.value = data.per_page
    isUsersLoaded.value = true
    getClientLevelPath()
}

const applySearch = _.debounce(function () {
    fetchData()
}, 300)

function handleSelectionChange(val) {
    userSelected.value = val
}

function handleDelete() {
    let user_w_subusers = []

    _.forEach(userSelected.value, u => {
        if (u.subusers.length > 0) {
            user_w_subusers.push(u)
        }
    })

    const user_mailes = userSelected.value.map(o => o.email).join(', ')

    ElMessageBox.confirm('This will permanently delete user(s): <br>' + user_mailes, 'Attention', {
        type: 'warning',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true
    }).then(() => {
        if (user_w_subusers.length > 0) {
            const user_w_sub_mailes = user_w_subusers.map(o => o.email).join(', ')

            ElMessageBox.confirm('These primary user(s) have subuser(s): <br>' + user_w_sub_mailes, 'Attention', {
                type: 'warning',
                confirmButtonText: 'Remove users with subusers',
                cancelButtonText: 'Cancel',
                dangerouslyUseHTMLString: true
            }).then(() => {
                removeUsers()
            })
        } else {
            removeUsers()
        }
    })


}

function removeUsers() {
    deleting.value = true
    const user_ids = userSelected.value.map(u => u._id)
    ssUserApi.destroyBulk(user_ids).then(() => {
        fetchData()
    }).catch(error => {
        console.error(error)
    }).finally(() =>
        deleting.value = false
    )
}

const showStepsModal = ref(false)
const userId = ref(null)

function handleShowSteps(user_id) {
    showStepsModal.value = true
    userId.value = user_id
}

const showLogModal = ref(false)

function handleShowLog(user_id) {
    showLogModal.value = true
    userId.value = user_id
}

const enabling = ref([])

function toggleEnable(user) {
    enabling.value[user._id] = true
    ssUserApi.toggleEnable({
        user_email: user.email,
        status: user.status
    }).then(() => {
        enabling.value[user._id] = false
    })
}

const enablingCoaching = ref([])
const enablingDirectedSpend = ref([])

function toggleCoaching(user) {
    enablingCoaching.value[user._id] = true
    ssUserApi.toggleCoaching({
        user_email: user.email,
        coaching: user.coaching
    }).then(() => {
        enablingCoaching.value[user._id] = false
    })
}

function toggleDirectedSpend(user) {
    enablingDirectedSpend.value[user._id] = true
    ssUserApi.toggleDirectedSpend({
        user_email: user.email,
        directed_spend: user.directed_spend
    }).then(() => {
        enablingDirectedSpend.value[user._id] = false
    })
}

function getUserRole(user) {
    let role = user.type === 'normal' ? 'Primary' : 'Sub Account'
    return role += user.is_fdm === true || user.is_fdm === 'true' ? '+ FDM' : ''
}

function modifyString(str, separator) {
    return str.split(separator).join(' <span>/</span> ')
}

function getClientLevelPath() {
    if (!(isClientLevelLoaded.value && isUsersLoaded.value)) {
        return false
    }
    users.value = users.value.map(user => {
        let pathArr = []

        if (user.client_level_id == null) {
            return user
        }
        const userClientLevel = clientLevels.value.find(level => level._id === user.client_level_id)
        if (userClientLevel == null) {
            return user
        }

        pathArr.push(userClientLevel.name)
        let parentLevelId = userClientLevel.parent_id

        while (parentLevelId !== null && parentLevelId !== undefined) {
            const parentLevel = clientLevels.value.find(level => level._id === parentLevelId)
            const parentName = parentLevel?.name
            parentLevelId = parentLevel?.parent_id
            pathArr = [...pathArr, parentName]
        }

        user.client_level = pathArr.reverse().join(' > ')
        return user
    })
    usersLoading.value = false
}
</script>
